import React, { useState, useEffect, useRef, useCallback } from "react";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";

import {
  SimpleGrid,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
  Text,
  Icon,
  HStack,
  Tooltip,
  Box,
  useBreakpointValue,
} from "@chakra-ui/react";

import CustomDropzone from "../../../../../../components/CustomDropzone";
import { CustomButton } from "../../../../../../components/CustomButton";
import { CustomSelectWField } from "../../../../../../components/CustomSelectWField";
import { FocusError } from "../../../../../../components/FocusError";
import { CustomInputWField } from "../../../../../../components/CustomInputWField";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { hasFormChanged } from "../../../../../../utils/hasFormChanged";
import { useAppContext } from "../../../../../../../Context/MessageContextProvider";
import ChangeRadio from "../../../../../../components/ChangeRadio";
import { numeroProcesso } from "../../../../../../utils/masks";
import SituacaoJuridicaService from "../../../../../../../main/services/SituacaoJuridicaService";
import CriancaService from "../../../../../../../main/services/CriancaService";
import { IoHelpCircle } from "react-icons/io5";
import { CustomTextArea } from "../../../../../../components/CustomTextArea";
import { deleteUrlParam } from "../../../../../../utils/deleteUrlParam";
import { useHistory, useLocation } from "react-router-dom";
import { PopoverGenero } from "../../../../../../components/PopoverGenero";
import { servicoAcolhimentoFunc } from "../../../../../../../helpers/Funcionalidades";

const suportedFormats = [
  ".png",
  ".bmp",
  ".tiff",
  ".jpg",
  ".jpeg",
  ".doc",
  ".docx",
  ".txt",
  ".dot",
  ".pdf",
  ".xml",
  ".xls",
  ".xlam",
  ".ods",
];

export const SituacaoJuridicaModal = ({
  isOpen,
  onOpen,
  onClose,
  onCloseFiliacoes,
  form,
  filiacoes,
  setNovaFiliacao,
  initialFiliacao,
  roleKeycloack,
  idCrianca,
  fetchProcessosProcedimentoData,
  fetchAtribuicao,
}) => {
  const [servicosData, setServicosData] = useState({
    options: [],
    isLoading: false,
  });

  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");
  const idAnexo = searchParams.get("idAnexo");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [hintCompartilhar, setHintCompartilhar] = useState(false);

  const [irmaosData, setIrmaosData] = useState({
    options: [],
    isLoading: false,
  });

  const schema = Yup.object().shape({
    numeroProcesso: Yup.string()
      .required("Este campo é obrigatório!")
      .test("is-valid-numerics", "Número inválido", function (value) {
        if (value) {
          let temp = value.replace(/\D/g, "").slice(0, 20);

          const numericCount = temp.length;

          return numericCount >= 20;
        }
        return true;
      }),
    liminarFamiliaSubstituta: Yup.string().required(
      "Este campo é obrigatório!"
    ),
    sentenca: Yup.string()
      .required("Este campo é obrigatório!")
      .test(
        "sentenca-requerida",
        "Por favor, marque a opção sim para sentença e selecione o tipo correto",
        function (value) {
          const { transitoJulgado } = this.parent;
          if (transitoJulgado === "S" && (value === "N" || value === "A")) {
            return false;
          }
          return true;
        }
      ),
    compartilharProcesso: Yup.string().test(
      "required-if-irmaos",
      "Este campo é obrigatório!",
      function (value) {
        if (
          irmaosData &&
          irmaosData.options &&
          irmaosData.options.length > 0 &&
          !(
            roleKeycloack &&
            roleKeycloack.includes(servicoAcolhimentoFunc.visualizarParteTJ)
          )
        ) {
          return !!value;
        }
        return true;
      }
    ),
    irmaos: Yup.array()
      .nullable()
      .test("irmaosArrayLength", "Este campo é obrigatório!", function (value) {
        const { compartilharProcesso } = this.parent;

        if (value.length === 0 && compartilharProcesso === "S") {
          return false;
        }

        return true;
      }),
    recurso: Yup.string().required("Este campo é obrigatório!"),
    transitoJulgado: Yup.string().required("Este campo é obrigatório!"),

    idSituacaoJuridica: Yup.object()
      .nullable()
      .required("Este campo é obrigatório!"),
    idTipoSentenca: Yup.object().when("sentenca", {
      is: (sentenca) => sentenca === "S",
      then: Yup.object().nullable().required("Este campo é obrigatório"),
      otherwise: Yup.object().nullable(),
    }),
    informacaoComplementar: Yup.string(),
    anexoPeticaoInicial: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoDecisaoLiminar: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoSentenca: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoPecaRecursal: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoCertidaoTransitoJulgado: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
  });

  useEffect(() => {
    if (idCrianca) {
      const handleGetIrmaosProcessoProcedimento = async () => {
        setIrmaosData((prevIrmaosData) => ({
          ...prevIrmaosData,
          isLoading: true,
        }));
        await CriancaService.getIrmaosProcessoProcedimento(idCrianca).then(
          (res) => {
            setIrmaosData({ options: res.data, isLoading: false });
          }
        );
      };
      handleGetIrmaosProcessoProcedimento();
    }
  }, [idCrianca]);

  const {
    cadastrarProcessosProcedimentos,
    setCadastrarProcessosProcedimentos,
  } = useAppContext();

  const [initialValues] = useState({
    numeroProcesso: "",
    anexoPeticaoInicial: null,
    liminarFamiliaSubstituta: "",
    sentenca: "",
    recurso: "",
    transitoJulgado: "",
    anexoDecisaoLiminar: null,
    anexoSentenca: null,
    anexoPecaRecursal: null,
    anexoCertidaoTransitoJulgado: null,
    informacaoComplementar: "",
    compartilharProcesso: "",
    irmaos: [],
    idSituacaoJuridica: "",
    idTipoSentenca: "",
    irmaosData: irmaosData,
  });
  const [loading, setLoading] = useState(false);

  const backAlert = () => {
    if (cadastrarProcessosProcedimentos) {
      Swal.fire({
        title: "Tem certeza que deseja cancelar?",
        text: "As informações serão perdidas.",
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: "#169499",
        cancelButtonColor: "#169499",
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
      }).then((result) => {
        if (result.isConfirmed) {
          onClose();
          setCadastrarProcessosProcedimentos(false);
        }
      });
    } else {
      onClose();
    }
  };

  const addProcessoProcedimento = async (id, valuesToSend) => {
    try {
      setLoading(true);
      const res = await CriancaService.cadastrarProcessoProcedimento(
        id,
        valuesToSend
      );
      deleteUrlParam("idAnexo", history);
      (["13", "14", "15", "16", "17"].includes(idAnexo) ||
        ["46", "47"].includes(idTipoPendencia)) &&
        history.replace(location.pathname);
      Swal.fire({
        title: "Sucesso!",
        text: "Registro cadastrado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {});
      await CriancaService.getAtribuicaoCompetenciaAtualizarPP(
        id,
        res?.data?.id
      );
      onClose();

      await Promise.all([fetchAtribuicao()]).then(() => {
        fetchProcessosProcedimentoData();
      });
    } catch (error) {
      console.log(error);
      const errorMessage =
        error?.response?.data?.status !== 500 &&
        error?.response?.data?.field !== "processoProcedimentoDuplicado"
          ? JSON.parse(error?.response?.data?.message)
          : "";
      console.log(error?.response);
      Swal.fire({
        title: "Atenção!",
        text: error?.response?.data?.message
          ? errorMessage?.processoProcedimentoDuplicado ||
            error.response.data.message
          : "",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  const handleGetServicos = () => {
    setServicosData({ ...servicosData, isLoading: true });

    SituacaoJuridicaService.pesquisarSituacaoJuridica("", "ativos", "").then(
      (res) => {
        const tempOptions = res.data.map((servicos) => {
          return {
            id: servicos?.id,
            pessoaRestricao: servicos.pessoaRestricao,
            label: (
              <HStack justifyContent="space-between" alignItems="center">
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                  isTruncated
                >
                  {servicos.descricao}
                </Text>
                <Tooltip
                  label={servicos.descricao}
                  fontSize="md"
                  textAlign={"justify"}
                  placement="top"
                  hasArrow
                >
                  <span className="hint-tooltip">
                    <Icon
                      size={20}
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          };
        });
        setServicosData({ options: tempOptions, isLoading: false });
      }
    );
  };

  const handleSubmit = (values) => {
    const {
      numeroProcesso,
      liminarFamiliaSubstituta,
      sentenca,
      recurso,
      transitoJulgado,
      anexoDecisaoLiminar,
      anexoSentenca,
      anexoPecaRecursal,
      anexoCertidaoTransitoJulgado,
      informacaoComplementar,
      compartilharProcesso,
      irmaos,
      idSituacaoJuridica,
      idTipoSentenca,
      anexoPeticaoInicial,
    } = values;
    const objetosIrmaos = irmaos?.map((jsonString) => {
      const objeto = JSON.parse(jsonString);
      return { ...objeto, compartilhado: "S" };
    });

    const valuesToSend = {
      idCrianca: idCrianca,
      numeroProcesso: numeroProcesso,
      liminarFamiliaSubstituta: liminarFamiliaSubstituta,
      sentenca: sentenca,
      recurso: recurso,
      transitoJulgado: transitoJulgado,
      anexoPeticaoInicial: anexoPeticaoInicial,
      anexoDecisaoLiminar:
        liminarFamiliaSubstituta === "S" && anexoDecisaoLiminar
          ? anexoDecisaoLiminar
          : null,
      anexoSentenca: sentenca === "S" && anexoSentenca ? anexoSentenca : null,
      anexoPecaRecursal:
        recurso === "S" && anexoPecaRecursal ? anexoPecaRecursal : null,
      anexoCertidaoTransitoJulgado:
        transitoJulgado === "S" && anexoCertidaoTransitoJulgado
          ? anexoCertidaoTransitoJulgado
          : null,
      informacaoComplementar: informacaoComplementar
        ? informacaoComplementar.trim()
        : "",
      compartilharProcesso:
        irmaos && irmaos.length > 0 && compartilharProcesso === "S"
          ? "S"
          : compartilharProcesso === "N"
          ? "N"
          : "",
      irmaos: compartilharProcesso === "S" ? objetosIrmaos : [],
      idSituacaoJuridica: idSituacaoJuridica ? idSituacaoJuridica.id : "",
      idTipoSentenca: idTipoSentenca ? idTipoSentenca.id : "",
      idAnexoDecisaoLiminar: 0,
      atualizarAnexoDecisaoLiminar: true,
      removerAnexoDecisaoLiminar: false,
      idAnexoSentenca: 0,
      atualizarAnexoSentenca: true,
      removerAnexoSentenca: false,
      idAnexoPecaRecursal: 0,
      atualizarAnexoPecaRecursal: true,
      removerAnexoPecaRecursal: false,
      idAnexoCertidaoTransitoJulgado: 0,
      atualizarAnexoCertidaoTransitoJulgado: true,
      removerAnexoCertidaoTransitoJulgado: false,
      idAnexoPeticaoInicial: 0,
      atualizarAnexoPeticaoInicial: true,
      removerAnexoPeticaoInicial: false,
    };

    addProcessoProcedimento(idCrianca, valuesToSend);
  };
  const endOfModalRef = useRef(null);

  const handleScrollToBottom = useCallback(() => {
    if (endOfModalRef.current) {
      endOfModalRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        scrollBehavior={"inside"}
      >
        <ModalOverlay />
        <ModalContent
          p={{ base: "5px", sm: "15px", md: "25px" }}
          w={{ base: "100%", lg: "50%" }}
          bgColor="#FFF"
        >
          <ModalHeader
            p="0px"
            fontWeight={700}
            fontSize="14px"
            lineHeight="19.6px"
            color="#169499"
          >
            {"Cadastrar Processo/Procedimento".toUpperCase()}
          </ModalHeader>
          <ModalBody
            p="0px"
            mt="25px"
            minH={{ base: "0px", md: "200px" }}
            pl="1px"
          >
            <Formik
              validationSchema={schema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {(props) => (
                <Form style={{ width: "100%" }}>
                  {setCadastrarProcessosProcedimentos(
                    hasFormChanged(initialValues, props.values)
                  )}

                  <FocusError />
                  <Grid templateColumns="repeat(6, 1fr) 1fr" gap={2}>
                    <GridItem
                      display="flex"
                      border="1px solid #b3b3b3"
                      rounded="xl"
                      flexDirection={{ base: "column", md: "row" }}
                      px={2}
                      py={3}
                      gap={4}
                      colSpan={7}
                    >
                      <Grid templateColumns="repeat(8, 1fr)" gap={4}>
                        <GridItem colSpan={{ base: 8, md: 4 }}>
                          <CustomInputWField
                            fieldName="numeroProcesso"
                            label="Nº do Processo/Procedimento*"
                            placeholder="Informe o Número"
                            onChange={(e) =>
                              props.setFieldValue(
                                "numeroProcesso",
                                numeroProcesso(e.target.value)
                              )
                            }
                          />
                        </GridItem>
                        <GridItem colSpan={{ base: 8, md: 4 }}>
                          <CustomSelectWField
                            fieldName="idSituacaoJuridica"
                            label="Tipo*"
                            placeholder="Selecione o Tipo"
                            getOptionLabel={(option) => option?.label}
                            getOptionValue={(option) => option.id}
                            isLoading={servicosData.isLoading}
                            options={servicosData.options}
                            onChange={(value) => {
                              props.setFieldValue("idSituacaoJuridica", value);
                            }}
                            onFocus={() => handleGetServicos()}
                          />
                        </GridItem>
                        <GridItem colSpan={{ base: 8, md: 2 }}>
                          <CustomDropzone
                            whiteSpace={true}
                            formProps={props}
                            title={"Anexar Petição Inicial"}
                            fieldName={"anexoPeticaoInicial"}
                            height="42px"
                            titleText={"Anexar Petição Inicial"}
                          />
                        </GridItem>
                      </Grid>
                    </GridItem>
                    <GridItem colSpan={7}>
                      <ChangeRadio
                        setFieldValue={props.setFieldValue}
                        values={props.values}
                        fieldName={"liminarFamiliaSubstituta"}
                        fieldNameAnexo={"anexoDecisaoLiminar"}
                        label={
                          "Há Decisão Liminar de Colocação em Família Substituta?*"
                        }
                        labelAnexo={"Anexar Decisão Liminar"}
                      />
                    </GridItem>
                    <GridItem colSpan={7}>
                      <ChangeRadio
                        setFieldValue={props.setFieldValue}
                        values={props.values}
                        fieldName={"sentenca"}
                        fieldNameAnexo={"anexoSentenca"}
                        label={"Sentença?*"}
                        labelAnexo={"Anexar Sentença"}
                      />
                    </GridItem>
                    <GridItem colSpan={7}>
                      <ChangeRadio
                        setFieldValue={props.setFieldValue}
                        values={props.values}
                        fieldName={"recurso"}
                        fieldNameAnexo={"anexoPecaRecursal"}
                        label={"Recurso?*"}
                        labelAnexo={"Anexar Peça Recursal"}
                      />
                    </GridItem>
                    <GridItem colSpan={7}>
                      <ChangeRadio
                        setFieldValue={props.setFieldValue}
                        values={props.values}
                        fieldName={"transitoJulgado"}
                        fieldNameAnexo={"anexoCertidaoTransitoJulgado"}
                        label={"Trânsito em Julgado?*"}
                        labelAnexo={"Anexar Certidão de Trânsito em Julgado"}
                      />
                    </GridItem>
                    <GridItem
                      colSpan={7}
                      border="1px solid #b3b3b3"
                      rounded="xl"
                      px={2}
                      py={3}
                    >
                      <CustomTextArea
                        fieldName={"informacaoComplementar"}
                        label={"Informações Complementares"}
                        placeholder={"Informe as Informações Complementares"}
                        setIsTextareaFocused={setIsTextareaFocused}
                        maxLength={255}
                        isTextareaFocused={isTextareaFocused}
                        fontWeightLabel={500}
                        colorLabel="#5F6377"
                        fontSizeLabel={"14px"}
                        lineHeightLabel={"19.6px"}
                      />
                    </GridItem>
                    {irmaosData && irmaosData.options.length > 0 && (
                      <GridItem colSpan={7}>
                        <ChangeRadio
                          setFieldValue={props.setFieldValue}
                          values={props.values}
                          fieldName={"compartilharProcesso"}
                          fieldNameCheck={"irmaos"}
                          irmaosData={irmaosData}
                          irmaosDataBoolean={true}
                          idCrianca={idCrianca}
                          label={
                            <Flex>
                              <Text textTransform="uppercase">
                                Deseja Compartilhar Processo/Procedimento na
                                Ficha do Irmão?
                              </Text>
                              {isMobile ? (
                                <PopoverGenero
                                  isOpen={hintCompartilhar}
                                  setIsOpen={setHintCompartilhar}
                                  flex={true}
                                  handleClick={() =>
                                    setHintCompartilhar(!hintCompartilhar)
                                  }
                                  labelTextoGenero={
                                    "Ao compartilhar um processo/procedimento com irmão(s), todas as informações serão importadas para ficha do(s) irmão(s) e as atualizações serão feitas de forma simultânea, ou seja, ao acessar qualquer uma das fichas e atualizar as informações sobre um processo/procedimento compartilhado, automaticamente essas informações também serão atualizadas na(s) ficha(s) do(s) irmão(s). Caso o processo/procedimento seja descompartilhado, as informações serão excluídas somente da ficha descompartilhada."
                                  }
                                />
                              ) : (
                                <Tooltip
                                  label={
                                    "Ao compartilhar um processo/procedimento com irmão(s), todas as informações serão importadas para ficha do(s) irmão(s) e as atualizações serão feitas de forma simultânea, ou seja, ao acessar qualquer uma das fichas e atualizar as informações sobre um processo/procedimento compartilhado, automaticamente essas informações também serão atualizadas na(s) ficha(s) do(s) irmão(s). Caso o processo/procedimento seja descompartilhado, as informações serão excluídas somente da ficha descompartilhada."
                                  }
                                  fontSize="md"
                                  textAlign={"justify"}
                                  maxW={"400px"}
                                >
                                  <span className="hint-tooltip">
                                    <Icon
                                      aria-label="Aviso de pendente"
                                      color="#6B6C71"
                                      as={IoHelpCircle}
                                      boxSize={{ base: "20px" }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </Flex>
                          }
                          handleScrollToBottom={handleScrollToBottom}
                        />
                        {irmaosData &&
                          roleKeycloack &&
                          roleKeycloack.includes(
                            servicoAcolhimentoFunc.visualizarParteTJ
                          ) && (
                            <Box
                              position="absolute"
                              top={0}
                              left={0}
                              right={0}
                              bottom={0}
                              opacity={0.3}
                              bg="gray"
                              zIndex={1}
                              cursor="not-allowed"
                              borderRadius="md"
                            />
                          )}
                      </GridItem>
                    )}
                  </Grid>
                  <Flex justifyContent="end">
                    <SimpleGrid
                      mt="25px"
                      columns={2}
                      spacing="15px"
                      w={{ base: "100%", lg: "40%" }}
                    >
                      <CustomButton onClick={backAlert} label="Cancelar" />
                      <CustomButton
                        type="submit"
                        translate="no"
                        isLoading={loading}
                        label="Salvar"
                      />
                    </SimpleGrid>
                  </Flex>
                  <Box ref={endOfModalRef} />
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
